import type { Block, FormBlock, FormLine, FormPanel, NoteBlock, Tone } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import type { ScreenData, ScreenLoader } from './provenance'
import { callerBeside, failurePanel, writeRefusal } from './writes'

/**
 * Recognition › Match simulator (`rc-sim`), over
 * `POST /v1/admin/recognition/simulate` (handler
 * `internal/server/admin_recognition.go`).
 *
 * # It is a READ, and the console spent a while believing otherwise
 *
 * This screen was unwired with the recorded reason *"`rc-sim` is a POST and
 * therefore a control, which belongs in a change that argues for its shape"*.
 * The endpoint's own description refutes it:
 *
 * > *"A read-only probe of the recognition engine… and WRITES NOTHING — no
 * > candidate, no feedback, no decision. POST because the synthetic credit is a
 * > body, not because anything changes."*
 *
 * Its permission is `catalog.read`. The console was refusing a read on the
 * strength of its HTTP verb (#208).
 *
 * # What it is for
 *
 * Two questions, and they are the same question from either end of time. *"Why
 * did this credit get tagged TikTok?"* — asked six months later, against the
 * catalog as it stood then. And *"what would this catalog change have done?"* —
 * asked before publishing one. Both are answered by running the same pure
 * matcher the ingest path runs, against a catalog resolved at an instant the
 * operator names.
 *
 * # `as_of` is required and is never this console's idea
 *
 * *"REQUIRED and never defaulted to now. Answering a question about June with
 * July's catalog produces a different answer that looks like the right one."*
 * So there is no default, no "today" button, and no silent midnight: an
 * unparseable instant is refused here with a sentence rather than sent and
 * refused there with a 400.
 *
 * # An `unrecognized` result is a statement about the CATALOG
 *
 * The single most dangerous thing this screen can be read as saying is "this
 * credit would go untagged", and that is not what it says. The customer's own
 * orchestrator mapping is not consulted — it is per-customer state and never
 * global (invariant 11b), and this screen has no customer scope. So
 * `unrecognized` means *the catalog alone does not tag this*, and the
 * distinction travels on the result rather than in a note further up the page.
 *
 * The backend writes that sentence itself, along with two others, and this
 * module renders all three VERBATIM. They are the API's own account of its
 * exclusions; paraphrasing them here would put this console's wording on a
 * claim that is not this console's to make.
 */

/** `catalog.read` — Support, and everything that nests above it. */
const PERM_READ = 'catalog.read'

/**
 * The role named on the refusal.
 *
 * Support, the widest holder, and the endpoint agrees with the choice in its
 * own words: the simulator writes nothing, which is *"why it is safe to hand to
 * support during a live call"*. Naming Ops here would send an agent to ask for
 * more than the act needs.
 */
const ROLE = 'Support'

export const SIMULATE_PATH = '/recognition/simulate'

const SOURCE = 'go-wallet recognition matcher (POST /v1/admin/recognition/simulate)'

// ---------------------------------------------------------------------------
// The wire
// ---------------------------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * An integer this console can state exactly, or nothing.
 *
 * `Number.isSafeInteger`, for the reason #207 sets out at length: `JSON.parse`
 * has already rounded a Go `int64` past 2^53 by the time it arrives, and
 * `Number.isInteger` says yes to the rounded value. `entry_id` is the row an
 * operator opens next.
 */
function whole(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

/**
 * The confidence tier, as a tone.
 *
 * Green ONLY for `certain`, which the engine gives only for an exact debtor-IBAN
 * match. `high` is the ceiling for a name match and *"never authorises
 * anything"*, so it is not the same kind of answer and must not read as one.
 *
 * `unrecognized` is `n` and deliberately not `a`. It is not a failure and not a
 * warning — it is a fact about the catalog at that instant, and an amber chip
 * would teach an operator that running the simulator on an unlisted payer means
 * something is wrong.
 *
 * A tier this console does not know is `a`: it is not `certain`, and the only
 * value that may render green is one we recognise as meaning certain.
 */
function confidenceTone(confidence: string): Tone {
  if (confidence === 'certain') return 'g'
  if (confidence === 'unrecognized') return 'n'
  return 'a'
}

/**
 * What the operator typed, refused before it is sent, or `null` when it may go.
 *
 * Every branch is a sentence read at the panel rather than a 400 read after the
 * click. The three required fields are required by the backend and each is
 * required for a stated reason, so the refusal repeats the reason rather than
 * the field name.
 */
export function simulationRefusal(v: Readonly<Record<string, string>>): string | null {
  const asOf = (v['as_of'] ?? '').trim()
  if (asOf === '') {
    return 'Nothing was sent. An instant is required and is never defaulted to now — answering a question about June with July’s catalog produces a different answer that looks like the right one.'
  }
  if (Number.isNaN(new Date(asOf).getTime())) {
    return `Nothing was sent. "${asOf}" is not an instant this console can read. Use an RFC-3339 timestamp — 2026-07-31T14:02:00Z — because the catalog is resolved at the moment you name and a date alone would silently mean midnight.`
  }

  const amount = (v['amount_minor'] ?? '').trim()
  if (!/^[0-9]+$/.test(amount) || amount === '0') {
    return `Nothing was sent. The amount must be a positive whole number of MINOR units — 4125 for €41.25 — and "${amount === '' ? '' : amount}" is not one. It is not scaled here: the currency's own exponent decides what a minor unit is, and this console never assumes two decimal places (invariant 3).`
  }

  // Exact, or not sent. `/^[0-9]+$/` accepts a digit string of any length, and
  // `bodyFor` puts it through `Number()` — so an amount past 2^53 is ROUNDED on
  // the way out and the screen answers about a payment the operator did not
  // describe. That is #207's failure one field along: the guard adequate for a
  // shape becomes load-bearing the moment the value is sent somewhere.
  //
  // Refused rather than clamped or scaled, and `writes.ts` already settled the
  // reasoning for every amount on this console: "Dropping the row is
  // wrong-but-loud; sending a rounded amount is wrong-and-quiet." A simulator
  // exists to answer precisely; one that quietly re-asks its own question with
  // different figures is worse than one that declines.
  if (BigInt(amount) > BigInt(Number.MAX_SAFE_INTEGER)) {
    return `Nothing was sent. "${amount}" is past the largest whole number this console can carry to the backend without losing digits (${String(Number.MAX_SAFE_INTEGER)}), so it would arrive rounded and the answer would be about a different payment. Nothing is scaled or truncated here.`
  }

  const currency = (v['currency'] ?? '').trim()
  if (!/^[A-Za-z]{3}$/.test(currency)) {
    return `Nothing was sent. A three-letter ISO-4217 code is required and is never defaulted (invariant 4): catalog entries are scoped by currency, so the same payer can legitimately be a different platform in each. "${currency}" is not one.`
  }
  return null
}

/** The synthetic credit, as the body wants it. Optional fields are omitted when blank. */
function bodyFor(v: Readonly<Record<string, string>>): Record<string, unknown> {
  const body: Record<string, unknown> = {
    as_of: (v['as_of'] ?? '').trim(),
    amount_minor: Number((v['amount_minor'] ?? '').trim()),
    currency: (v['currency'] ?? '').trim().toUpperCase(),
  }
  for (const key of [
    'debtor_name', 'debtor_iban', 'debtor_bic',
    'remittance_unstructured', 'remittance_structured', 'end_to_end_id',
  ]) {
    const value = (v[key] ?? '').trim()
    // Omitted rather than sent empty. Every one of these is optional on the
    // wire — "the honest reflection of real SEPA and SWIFT traffic" — and an
    // empty string is a value the matcher would compare against.
    if (value !== '') body[key] = value
  }
  return body
}

/**
 * The result, as lines an operator reads.
 *
 * # Why this is a panel and not the design's `sim` block
 *
 * The design draws a two-pane `sim` with a six-layer ladder, and `Sim.tsx`
 * states the discipline that shape exists for: *"Every layer is listed,
 * including the ones that never ran… 'Not evaluated' is the answer to 'why
 * didn't rule 4 fire'."*
 *
 * **This endpoint reports only the layer that fired.** It carries `matched_by`
 * and nothing about the others, so five of the six rows could only be filled
 * from the design corpus — and for a layer that was never evaluated, the
 * corpus's *"would have matched entry 613 v2"* is not merely unavailable, it is
 * false. Rendering it beside a real result is the failure `rc-cand` refuses in
 * the same words.
 *
 * Strip the ladder and what is left is a heading, an outcome, a reference and
 * prose — which is exactly a `FormPanel`. So the block is unemitted, named as
 * absent below, and the result carries every field the response actually has.
 */
export function composeResult(payload: unknown): FormPanel {
  if (typeof payload !== 'object' || payload === null) {
    return {
      tone: 'r',
      heading: 'The simulator returned a shape this console does not know',
      lines: [],
      note: 'Nothing was written — this endpoint writes nothing at all — but nothing can be read from the answer either. That is a defect rather than a result, and it is not a statement about the payer you typed.',
      final: false,
    }
  }
  const r = payload as Record<string, unknown>

  const confidence = text(r['confidence'])
  const platform = text(r['platform_id'])
  const entries = whole(r['catalog_entries'])
  const entryID = whole(r['entry_id'])
  const entryVersion = whole(r['entry_version'])

  const lines: FormLine[] = [
    [
      'decision',
      confidence === '' ? '— the answer carried no confidence tier' : confidence,
      confidence === 'unrecognized'
        ? 'the catalog alone does not tag this payer at that instant'
        : 'the tier the matcher reached, not a verdict about a real credit',
    ],
    [
      'platform',
      platform === '' ? '— none' : platform,
      platform === '' ? 'no catalog entry fired' : 'what the catalog would have tagged this as',
    ],
    [
      'matched by',
      text(r['matched_by']) === '' ? '— not stated' : text(r['matched_by']),
      'which matching layer produced it',
    ],
    [
      'compared',
      text(r['compared']) === '' ? '— not stated' : text(r['compared']),
      'the normalised string the engine actually compared — most “why was this tagged wrong” questions end here',
    ],
    [
      'catalog entry',
      entryID === null
        ? '— none fired'
        : `${String(entryID)}${entryVersion === null ? '' : ` v${String(entryVersion)}`}`,
      'absent rather than zero when no row fired: an entry id of 0 is a row that does not exist',
    ],
    [
      'catalog size at that instant',
      entries === null ? '— not stated' : `${String(entries)} live entr(y/ies)`,
      // The endpoint asks for this to be visible in as many words, and says why.
      'an empty catalog returns unrecognised for every input, which reads as a broken matcher unless this is on screen',
    ],
    ['as of', text(r['as_of']) === '' ? '— not echoed' : text(r['as_of']), 'the catalog was resolved at this instant, not at now'],
  ]

  const route = r['route']
  if (typeof route === 'object' && route !== null) {
    const o = route as Record<string, unknown>
    lines.push([
      'arrived through',
      `${text(o['orchestrator_id'])} · ${text(o['confidence'])}`,
      // FR-3.3b. The route's confidence is not the decision's, and conflating
      // them is how "we know how the money arrived" becomes "we know who earned
      // it".
      'the ROUTE’s confidence, which is separate from the decision’s — we can be certain of the route and know nothing about who earned the money',
    ])
  }

  // The backend's own three sentences, verbatim and in its order. They are the
  // API's account of its own exclusions; rewording them here would put this
  // console's phrasing on a claim that is not this console's to make.
  const note = [text(r['learned_note']), text(r['as_of_note']), text(r['write_note'])]
    .filter((s) => s !== '')
    .join(' ')

  return {
    tone: confidenceTone(confidence),
    heading: platform === ''
      ? 'The catalog alone does not tag this payer'
      : `The catalog would tag this as ${platform}`,
    lines,
    note: note === ''
      ? 'The answer carried none of the notes this endpoint normally sends about what it did and did not consult. Read the result as narrower than it looks: this screen has no customer scope, so a customer’s own orchestrator mapping was not part of it.'
      : note,
    // NEVER final. Nothing was written, and running the same credit against a
    // different instant — or a different credit against the same one — is the
    // entire use of this screen. A control that died after one run would be a
    // simulator you can use once.
    final: false,
  }
}

function simulateSubmit(api: ApiClient) {
  return async (v: Readonly<Record<string, string>>): Promise<FormPanel> => {
    const refusal = simulationRefusal(v)
    if (refusal !== null) {
      return {
        tone: 'a',
        heading: 'Nothing was sent',
        lines: [],
        note: refusal,
        final: false,
      }
    }
    try {
      return composeResult(await api.post<unknown>(SIMULATE_PATH, bodyFor(v)))
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      // The shared panel, which surfaces a `decision_ref` if one came back
      // (law 13). Nothing here can be written, so its "nothing was written"
      // wording is true by construction on every branch.
      //
      // `final` is FORCED false, and this is the one place this screen departs
      // from the shared panel. `final` asks whether there is anything left to
      // submit, and `failurePanel` answers it from the status: a 4xx means the
      // backend considered the request and refused it, so resending it
      // unchanged gets the same answer and the control dies.
      //
      // That is right for a write and wrong here. On this screen a 400 means
      // the operator typed something the matcher would not take — a currency
      // the backend spells differently, an instant before the catalog existed —
      // and the response is to change it and run again. There is no request to
      // resend UNCHANGED, because the whole control is the input. A dead button
      // after one 400 is a simulator you can use once.
      return { ...failurePanel(error), final: false }
    }
  }
}

const FIELD_NOTE_OPTIONAL =
  'Optional. Every field on a real inbound is optional in practice — that is the honest reflection of SEPA and SWIFT traffic, not defensive modelling. Left blank it is omitted, never sent as an empty string for the matcher to compare against.'

export function simulatorForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_READ, ROLE, 'Running the match simulator')
  return {
    t: 'form',
    title: 'Run Match() against a synthetic credit',
    lead: 'Runs the same pure matcher the ingest path runs, against the catalog as it stood at an instant you name. It writes nothing — no decision, no candidate, no feedback — which is why it is safe to run during a live call with a customer.',
    fields: [
      {
        name: 'as_of', label: 'Catalog as of', kind: 'text', required: true, mono: true,
        placeholder: '2026-07-31T14:02:00Z',
        note: 'An RFC-3339 instant, and there is deliberately no default and no “now” button. The catalog is resolved at the moment you name — answering a question about June with July’s catalog produces a different answer that looks like the right one. Give the time as well as the day: a date alone would silently mean midnight, and a catalog published at 14:02 is a different catalog from the one that stood at 00:00 the same morning.',
      },
      {
        name: 'amount_minor', label: 'Amount (minor units)', kind: 'text', required: true, mono: true,
        placeholder: '4125',
        note: 'A whole number of the currency’s own minor units — 4125 for €41.25. Never scaled here and never assumed to be two decimal places (invariant 3); the exponent belongs to the currency below.',
      },
      {
        name: 'currency', label: 'Currency', kind: 'text', required: true, mono: true,
        placeholder: 'EUR',
        note: 'An ISO-4217 code, never defaulted (invariant 4). Catalog entries are scoped by currency, so the same payer can legitimately be a different platform in each.',
      },
      { name: 'debtor_name', label: 'Debtor name', kind: 'text', required: false, mono: true, note: FIELD_NOTE_OPTIONAL },
      { name: 'debtor_iban', label: 'Debtor IBAN', kind: 'text', required: false, mono: true, note: 'Optional, and the strongest signal there is — an exact debtor-IBAN match is the only thing that reaches the `certain` tier.' },
      { name: 'debtor_bic', label: 'Debtor BIC', kind: 'text', required: false, mono: true, note: FIELD_NOTE_OPTIONAL },
      { name: 'remittance_unstructured', label: 'Remittance (unstructured)', kind: 'text', required: false, mono: true, note: FIELD_NOTE_OPTIONAL },
      { name: 'remittance_structured', label: 'Remittance (structured)', kind: 'text', required: false, mono: true, note: FIELD_NOTE_OPTIONAL },
      { name: 'end_to_end_id', label: 'End-to-end id', kind: 'text', required: false, mono: true, note: FIELD_NOTE_OPTIONAL },
    ],
    // No reason field, and its absence is the endpoint's rather than an
    // oversight: the call is audited "with a FIXED reason rather than an
    // operator-supplied one", because nothing is being decided. A reason box
    // here would be typed into and silently dropped.
    auditLine: 'admin.recognition.simulate · actor · no subject — nothing is acted on · reason fixed by the server',
    cta: 'Run Match()',
    ctaNote: 'Writes nothing. No decision row, no candidate, no feedback — the audit row exists because the call happened, not because anything changed (invariant 6). Run it as many times as you like, against as many instants as you like.',
    blocked,
    ...(blocked === '' ? { submit: simulateSubmit(api) } : {}),
  }
}

/**
 * Invariant 11b, stated where it can be misread rather than at the foot.
 *
 * The one sentence this screen must not be read as saying is "this credit would
 * go untagged". The backend puts the same caveat on every response, and this
 * note is what an operator has read BEFORE the first result arrives.
 */
const SCOPE_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Invariant 11b',
  title: 'An unrecognised result here is about the catalog, not about a customer',
  body: 'This screen has no customer scope, and a customer’s own orchestrator mapping is deliberately not consulted — it is per-customer state and never global. So “unrecognised” means the catalog ALONE does not tag this payer at that instant. It does not mean a real credit from this payer would go untagged: the customer’s own standing answer may well tag it, and no screen without a customer on it can tell you whether it would.',
  ref: 'FR-3.3b · the simulator is global; recognition is not only global',
}

/** What the design draws here that this endpoint does not answer for. */
const LADDER_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  kicker: 'Not shown',
  title: 'The layer-by-layer ladder the design draws is not on this wire',
  body: 'The designed screen shows all six matching layers, including the ones that never ran, because “not evaluated” is the answer to “why didn’t rule 4 fire”. This endpoint reports only the layer that DID fire. The other five could be filled from the design corpus, where a skipped layer carries a line like “would have matched entry 613 v2” — and for a layer that was never evaluated that line is not merely unavailable, it is untrue. So the ladder is absent rather than five-sixths invented, and the layer that fired is named on the result instead.',
  ref: 'the response carries matched_by, and nothing about the layers that did not',
}

const WRITES_NOTHING_NOTE: NoteBlock = {
  t: 'note',
  tone: 'g',
  kicker: 'Pure',
  title: 'The simulator writes nothing',
  body: 'No decision row, no candidate, no feedback. It reads a catalog version and returns what the matcher would return, which is why it is safe to hand to support during a live call. The call is audited because it happened, not because anything changed.',
  ref: 'recognition_decisions is unaffected by this screen',
}

export function composeSimulator(api: ApiClient, caller: Caller): ScreenData {
  const blocks: Block[] = [
    simulatorForm(api, caller),
    SCOPE_NOTE,
    LADDER_NOTE,
    WRITES_NOTHING_NOTE,
  ]
  return {
    blocks,
    // `probe`, and none of the other four would be true. Nothing has been read,
    // which is not an incident (`unread`), not a roadmap fact (`not_built`),
    // and not a partial read of something (`partial`). See `provenance.ts`.
    provenance: {
      kind: 'probe',
      source: SOURCE,
      reason: 'Nothing has been simulated yet, so there is no figure on this screen to be right or wrong about.',
    },
  }
}

/**
 * Reads the caller, and nothing else.
 *
 * There is no GET behind this screen and deliberately no stand-in for one. The
 * catalog page read next door would have given the screen a figure, and it
 * would have been the wrong figure: `GET /catalog/entries` answers no total, so
 * it reports a PAGE and not the catalog. The count that matters —
 * `catalog_entries` as of the instant asked about — is on the simulate
 * response, which is where the endpoint says it needs to be visible.
 */
export const loadMatchSimulator: ScreenLoader = async (api) =>
  composeSimulator(api, await callerBeside(api))
